import { useSavedUserSettings } from 'client/modules/localstorage/userSettings/useSavedUserSettings';
import { useCallback } from 'react';

/**
 * Manages the set of TP/SL order digests that have been suppressed from
 * auto-cancellation after exhausting all retries with a stale linked signer.
 *
 * Suppression persists to localStorage so that it survives page reloads.
 * The suppression is cleared when 1CT is successfully re-approved.
 */
export function useSuppressedOrphanedTpSlDigests() {
  const { savedUserSettings, setSavedUserSettings } = useSavedUserSettings();

  const suppressedDigests =
    savedUserSettings.trading.suppressedOrphanedTpSlDigests;

  /**
   * Records digests as suppressed with the current timestamp.
   */
  const suppressDigests = useCallback(
    (digests: string[]) => {
      if (digests.length === 0) return;

      const now = Date.now();
      setSavedUserSettings((prev) => {
        digests.forEach((digest) => {
          prev.trading.suppressedOrphanedTpSlDigests[digest] = now;
        });
        return prev;
      });
    },
    [setSavedUserSettings],
  );

  /**
   * Clears all suppressed digests (call on successful 1CT re-approval).
   */
  const clearSuppressedDigests = useCallback(() => {
    setSavedUserSettings((prev) => {
      prev.trading.suppressedOrphanedTpSlDigests = {};
      return prev;
    });
  }, [setSavedUserSettings]);

  /**
   * Removes suppressed digests that are no longer in the current open orders.
   * Keeps the suppressed list lean as orders get filled/cancelled via other means.
   */
  const pruneStaleDigests = useCallback(
    (currentOpenDigests: string[]) => {
      const openDigestSet = new Set(currentOpenDigests);
      setSavedUserSettings((prev) => {
        const next: Record<string, number> = {};
        for (const [digest, ts] of Object.entries(
          prev.trading.suppressedOrphanedTpSlDigests,
        )) {
          if (openDigestSet.has(digest)) {
            next[digest] = ts;
          }
        }
        prev.trading.suppressedOrphanedTpSlDigests = next;
        return prev;
      });
    },
    [setSavedUserSettings],
  );

  /**
   * Returns true if the given digest is currently suppressed.
   */
  const isDigestSuppressed = useCallback(
    (digest: string): boolean => {
      return digest in suppressedDigests;
    },
    [suppressedDigests],
  );

  return {
    suppressedDigests,
    suppressDigests,
    clearSuppressedDigests,
    pruneStaleDigests,
    isDigestSuppressed,
  };
}
